import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Tooltip as WmsTooltip } from "react-tooltip";
import { getAllTaskPriority, getAllTaskStatus, getAllTaskType, createTask, getTaskById, updateTaskAttachments, updateTask, handleDownloadFile, handleDownloadAllFiles, startWorkmanTimeLog, endWorkmanTimeLog } from '../../store/actions/picker.action';
import { getEffectiveRoleName, isDefaultSuperAdminRole, isSuperAdminRole } from '../../constants/roleConstants';
import { getAllCompanyDetails } from '../../store/actions/rateCard';
import { getSellersforMultipleCompany } from '../../store/actions/company';
import Select from 'react-select';
import { toast } from "react-toastify";
import { allowedDocExtensions } from '../../constant';
import _ from 'lodash';
import moment from 'moment';
import ChargeSeller from './ChargeSeller';
import { findConfiguration, getSelectList, computeAutoSelectedSeller } from '../../config/utils';
import PdfMergePopup from '../common/PDFMerge';
import LoaderSpinner from '../common/Loader';
import DatePickerComponent from '../common/DatePickerComponent';
import InlineFilePreview from '../common/PreviewAttachment';
import ViewImage from '../common/ViewImage';
import ChargesHistory from '../CarrierConnect/ChargesHistory';
import { Tooltip } from 'react-tooltip';
import AlertDialogModal from '../common/AlertDialog';
import TopUpModal from '../modal/TopUpModal';
import TopUpIconWhite from '../../assets/topUpWhite.svg';

/*istanbul ignore next */
const Tasks = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    let dispatch = useDispatch();
    const location = useLocation();
    const userDetails = useSelector((state) => state.auth.user);
    const [isEditable, setIsEditable] = useState(location?.state?.editable);
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImageRecord, setSelectedImageRecord] = useState({});
    const configurations = useSelector((state) => state?.auth?.configurations);
    const [maxSize, setMaxSize] = useState(0);
    const [showLoader, setShowLoader] = useState(false);
    const [showPopUp, setShowPopUp] = useState(false);
    const [formReset, setFormReset] = useState([]);
    const [showCharge, setShowCharge] = useState(false);
    const [showChargeAlert, setShowChargeAlert] = useState(false);
    const [showTopUpModal, setShowTopUpModal] = useState(false);
    const [formData, setFormData] = useState({
        taskTypeId: 0,
        taskPriorityId: 0,
        taskTitle: '',
        taskStatusId: 1,
        sku: '',
        quantity: '',
        instructions: '',
        attachments: [],
        amount: 0,
        companyId: 0,
        sellerId: 0,
        entryDate: new Date(),
        // Workman Time Log entries — each entry: { id?, startDateTime, endDateTime }
        workmanTimeLogs: [],
    });
    const [errors, setErrors] = useState({});
    const [showChargesDetails, setShowChargesDetails] = useState(false);
    const [companyList, setCompanyList] = useState([]);
    const [sellerList, setSellerList] = useState([]);
    const { allTaskPriorityList, allTaskStatusList, allTaskTypeList } = useSelector((state) => state.pickerReducer);
    const { getAllCompany } = useSelector((state) => state.RateCardReducer);
    const getSellersValue = useSelector((state) => state.company?.getMultipleCompanySellers);
    const MAX_FILE_COUNT = 10;

    // Resolve the "Workman Labor" task type id by name (case-insensitive contains)
    const workmanTaskTypeId = useMemo(() => {
        return allTaskTypeList?.find((t) => t?.typeName?.toLowerCase?.().includes('workman labor'))?.id;
    }, [allTaskTypeList]);

    const walletRechargeTaskTypeId = useMemo(() => {
        return allTaskTypeList?.find((taskType) => taskType?.typeName?.toLowerCase?.() === 'wallet recharge')?.id;
    }, [allTaskTypeList]);

    const isWorkmanLaborType = Boolean(
        workmanTaskTypeId !== undefined && Number(formData.taskTypeId) === Number(workmanTaskTypeId)
    );
    const isWalletRechargeTaskType = walletRechargeTaskTypeId !== undefined
        ? Number(formData.taskTypeId) === Number(walletRechargeTaskTypeId)
        : Number(formData.taskTypeId) === 4;

    // Show workman section read-only on Package/Product context tasks even when type is not Workman Labor.
    // Hidden entirely on the Create Task page (id=0) — time logs are tracked only on saved tasks.
    // Restricted to Super Admin and Warehouse users (Manager / Worker); other roles never see it.
    const hasWorkmanDataFromServer = Array.isArray(formData.workmanTimeLogs) && formData.workmanTimeLogs.length > 0;
    const isReturnContextTask = formData?.taskCategory === 'ReturnPackage' || formData?.taskCategory === 'ReturnProduct';
    const isExistingTask = Number(id) > 0;

    // Role-aware visibility for Workman Labor controls
    const userRoleName = getEffectiveRoleName(userDetails?.Value);
    const isSuperAdminUser = isSuperAdminRole(userDetails?.Value);
    const isDefaultSuperAdminUser = isDefaultSuperAdminRole(userDetails?.Value);
    const isWarehouseUser = userRoleName === 'Warehouse Manager' || userRoleName === 'Warehouse Worker';
    const canViewWorkmanSection = isSuperAdminUser || isWarehouseUser;

    const showWorkmanSection =
        isExistingTask &&
        canViewWorkmanSection &&
        (isWorkmanLaborType || (isReturnContextTask && hasWorkmanDataFromServer));
    const isWorkmanEditable = isWorkmanLaborType && isEditable;

    // Manual date/time inputs (and add/remove/save controls) only for non-warehouse users
    const canEditWorkmanInputs = isWorkmanEditable && !isWarehouseUser;
    // Start/End Task buttons visible to warehouse users and super admin on existing
    // Workman Labor tasks. Independent of `isEditable` because warehouse users open
    // the task in read-only mode (they're not allowed to edit task fields) but must
    // still be able to start/stop their own time tracking.
    const showWorkmanActionButtons =
        isWorkmanLaborType && Number(id) > 0 && (isWarehouseUser || isSuperAdminUser);
    // An "open" log is one that has start but no end — used to toggle Start/End button
    const openWorkmanLogIndex = (formData.workmanTimeLogs || []).findIndex(
        (l) => l?.startDateTime && !l?.endDateTime
    );
    const hasOpenWorkmanLog = openWorkmanLogIndex >= 0;

    const computeDurationMinutes = (start, end) => {
        if (!start || !end) return 0;
        const startM = moment(start).startOf('minute');
        const endM = moment(end).startOf('minute');
        if (!startM.isValid() || !endM.isValid() || endM.isSameOrBefore(startM)) return 0;
        return Math.round(endM.diff(startM, 'minutes', true));
    };

    const formatDurationLabel = (minutes) => {
        if (!minutes || minutes <= 0) return '0h 0m';
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours}h ${mins}m`;
    };

    const computeDurationLabel = (start, end) => {
        const mins = computeDurationMinutes(start, end);
        return mins > 0 ? formatDurationLabel(mins) : '';
    };

    const totalWorkmanMinutes = useMemo(() => {
        if (!Array.isArray(formData.workmanTimeLogs)) return 0;
        return formData.workmanTimeLogs.reduce(
            (sum, log) => sum + computeDurationMinutes(log?.startDateTime, log?.endDateTime),
            0
        );
    }, [formData.workmanTimeLogs]);

    const getRowRangeError = (log) => {
        if (!log?.startDateTime || !log?.endDateTime) return '';
        return moment(log.endDateTime).isSameOrBefore(moment(log.startDateTime))
            ? 'End datetime must be after start datetime'
            : '';
    };

    const handleWorkmanLogChange = (index, field, value) => {
        setFormData((prev) => {
            const logs = Array.isArray(prev.workmanTimeLogs) ? [...prev.workmanTimeLogs] : [];
            logs[index] = { ...logs[index], [field]: value };
            return { ...prev, workmanTimeLogs: logs };
        });
        // Clear field-specific error for this row when user edits
        setErrors((prev) => {
            if (!prev?.workmanTimeLogs?.[index]?.[field]) return prev;
            const next = { ...prev, workmanTimeLogs: [...prev.workmanTimeLogs] };
            next.workmanTimeLogs[index] = { ...next.workmanTimeLogs[index] };
            delete next.workmanTimeLogs[index][field];
            return next;
        });
    };

    const handleAddWorkmanLog = () => {
        setFormData((prev) => ({
            ...prev,
            workmanTimeLogs: [
                ...(Array.isArray(prev.workmanTimeLogs) ? prev.workmanTimeLogs : []),
                { startDateTime: null, endDateTime: null },
            ],
        }));
    };

    const handleRemoveWorkmanLog = (index) => {
        setFormData((prev) => {
            const logs = Array.isArray(prev.workmanTimeLogs) ? [...prev.workmanTimeLogs] : [];
            logs.splice(index, 1);
            return { ...prev, workmanTimeLogs: logs };
        });
        setErrors((prev) => {
            if (!prev?.workmanTimeLogs) return prev;
            const arr = [...prev.workmanTimeLogs];
            arr.splice(index, 1);
            return { ...prev, workmanTimeLogs: arr };
        });
    };

    // Ensure at least one editable log row exists when Workman Labor type is active in edit mode
    // (only for users who can edit inputs manually — warehouse users use Start/End Task buttons).
    // Skipped on the Create Task page since the section is hidden there and an empty row would
    // serialize to {null,null} causing a backend DateTime parse error.
    useEffect(() => {
        if (canEditWorkmanInputs && Number(id) > 0) {
            const logs = Array.isArray(formData.workmanTimeLogs) ? formData.workmanTimeLogs : [];
            if (logs.length === 0) {
                setFormData((prev) => ({
                    ...prev,
                    workmanTimeLogs: [{ startDateTime: null, endDateTime: null }],
                }));
            }
        }
    }, [canEditWorkmanInputs, formData.workmanTimeLogs]);

    const handleStartWorkmanTask = async () => {
        if (!Number(id)) return;
        try {
            setShowLoader(true);
            const res = await startWorkmanTimeLog({ taskId: Number(id) });
            setShowLoader(false);
            if (res?.data?.Status === 200) {
                toast.success('Task started');
                // Preserve current edit mode so the form doesn't flip to view-only
                // after the refetch (server's isEditable may differ from the
                // user's current edit-toggle state).
                fetchTaskDetails(true);
            } else {
                toast.error(res?.data?.Message || 'Failed to start task');
            }
        } catch (err) {
            setShowLoader(false);
            console.error('Start workman task failed:', err);
            toast.error('Failed to start task');
        }
    };

    const handleEndWorkmanTask = async () => {
        if (!Number(id)) return;
        try {
            setShowLoader(true);
            const res = await endWorkmanTimeLog({ taskId: Number(id) });
            setShowLoader(false);
            if (res?.data?.Status === 200) {
                toast.success('Task ended');
                fetchTaskDetails(true);
            } else {
                toast.error(res?.data?.Message || 'Failed to end task');
            }
        } catch (err) {
            setShowLoader(false);
            console.error('End workman task failed:', err);
            toast.error('Failed to end task');
        }
    };

    useEffect(() => {
        if (configurations && configurations.length > 0) {
            const maxSizeConfig = findConfiguration('TASK_ATTACHMENT_SIZE', configurations);
            if (maxSizeConfig) {
                setMaxSize(maxSizeConfig);
            }
        }
    }, [configurations]);

    useEffect(() => {
        if (Number(id) > 0) {
            fetchTaskDetails();
        }
    }, [id]);

    // When task details are loaded for an existing task, ensure seller list is populated
    // so the specific seller can be pre-selected (super-admin case)
    useEffect(() => {
        if (Number(id) > 0 && isSuperAdminUser) {
            const taskCompanyId = Number(formData.companyId) || 0;
            if (taskCompanyId > 0) {
                // fetch sellers for this company to ensure sellerList contains the task seller
                dispatch(getSellersforMultipleCompany([taskCompanyId]));
            }
        }
    }, [formData.companyId, id, isSuperAdminUser]);

    useEffect(() => {
        if (!allTaskPriorityList?.length) {
            dispatch(getAllTaskPriority());
        }

        if (!allTaskStatusList?.length) {
            dispatch(getAllTaskStatus());
        }

        if (!allTaskTypeList?.length) {
            dispatch(getAllTaskType());
        }
    }, []);

    useEffect(() => {
        if (isSuperAdminUser) {
            // Fetch full company list (same as Top Up)
            dispatch(getAllCompanyDetails(null, 500, 1));
            // If create page, fetch all sellers (companyId 0 = all)
            if (Number(id) === 0) {
                dispatch(getSellersforMultipleCompany([0]));
            }
        }
    }, [isSuperAdminUser, id]);

    // When company selection changes (for super admin create flow), fetch sellers for that company
    useEffect(() => {
        if (isSuperAdminUser && Number(id) === 0) {
            const selectedCompanyId = Number(formData.companyId) || 0;
            dispatch(getSellersforMultipleCompany([selectedCompanyId]));
            // Reset seller selection when company changes
            setFormData(prev => ({ ...prev, sellerId: 0 }));
        }
    }, [formData.companyId, isSuperAdminUser, id]);

    // Populate company and seller lists from Redux state (use Top Up data shapes)
    useEffect(() => {
        let companyData = getAllCompany?.Entity?.listCompanyMasterOrdersEntity || [];
        if (companyData.length) {
            // map to { value, label }
            setCompanyList(getSelectList(companyData, 'Id', 'name'));
        }

        let sellerData = getSellersValue?.Entity?.listSellerMasterOrdersEntity || [];
        if (sellerData.length) {
            setSellerList(getSelectList(sellerData, 'id', 'sellerAndCompany'));
            // Auto-select the lone seller when the selected company has exactly one
            const lone = computeAutoSelectedSeller(sellerData);
            if (lone) {
                setFormData(prev => (Number(prev?.sellerId) ? prev : { ...prev, sellerId: lone.id }));
            }
        } else {
            setSellerList([]);
        }
    }, [getAllCompany, getSellersValue]);

    // Validation helpers extracted to reduce cognitive complexity
    const isNonZero = (v) => Number(v) && Number(v) !== 0;
    const isNonEmptyString = (v) => typeof v === 'string' && v.trim() !== '';

    const validateAmountForTaskType = (formData, errors) => {
        if (isWalletRechargeTaskType && (!formData.amount || Number(formData.amount) <= 0)) {
            errors.amount = 'Amount is required for this task type';
        }
    };

    const validateCompanySellerForSuperAdmin = (formData, errors) => {
        const isSuperAdminCreate = isSuperAdminUser && Number(id) === 0;
        if (!isSuperAdminCreate) return;

        if (!formData.companyId || Number(formData.companyId) === 0) {
            errors.companyId = 'Company is required';
        }
        if (!formData.sellerId || Number(formData.sellerId) === 0) {
            errors.sellerId = 'Seller is required';
        }
    };

    const validateForm = (formData) => {
        const errors = {};
        // Required field checks
        const requiredChecks = [
            { key: 'taskTypeId', ok: isNonZero, msg: 'Task Type is required' },
            { key: 'taskPriorityId', ok: isNonZero, msg: 'Task Priority is required' },
            { key: 'taskTitle', ok: isNonEmptyString, msg: 'Task Title is required' },
            { key: 'taskStatusId', ok: isNonZero, msg: 'Task Status is required' },
        ];

        requiredChecks.forEach(({ key, ok, msg }) => {
            if (!ok(formData[key])) {
                errors[key] = msg;
            }
        });

        // Conditional validations
        validateAmountForTaskType(formData, errors);
        validateCompanySellerForSuperAdmin(formData, errors);

        // Workman Labor — at least one time log row with valid start/end (end > start).
        // Only validated on existing tasks: the section is hidden on Create, and
        // warehouse users track time via the Start/End Task buttons after save
        // rather than seeding rows manually.
        if (isWorkmanLaborType && Number(id) > 0 && !isWarehouseUser) {
            const logs = Array.isArray(formData.workmanTimeLogs) ? formData.workmanTimeLogs : [];
            if (logs.length === 0) {
                errors.workmanTimeLogs = [{ startDateTime: 'Start datetime is required', endDateTime: 'End datetime is required' }];
            } else {
                const rowErrors = [];
                let hasAnyRowError = false;
                logs.forEach((log, idx) => {
                    const rowErr = {};
                    if (!log?.startDateTime) {
                        rowErr.startDateTime = 'Start datetime is required';
                    }
                    if (!log?.endDateTime) {
                        rowErr.endDateTime = 'End datetime is required';
                    }
                    const rangeErr = getRowRangeError(log);
                    if (rangeErr) {
                        rowErr.endDateTime = rangeErr;
                    }
                    rowErrors[idx] = rowErr;
                    if (Object.keys(rowErr).length > 0) hasAnyRowError = true;
                });
                if (hasAnyRowError) {
                    errors.workmanTimeLogs = rowErrors;
                }
            }
        }

        setErrors(errors);
        return errors;
    };

    const openChargeSellerDialog = async () => {
        if (formData?.postCharges?.length > 0) {
            setShowChargeAlert(true);
        } else {
            setShowCharge(!showCharge);
        }
    };

    // Helper function to prepare task data for submission
    const prepareTaskData = () => {
        const applyWorkmanFields = (payload, { isUpdate }) => {
            // Backend expects workmanTimeLogs as an array of { id?, startDateTime, endDateTime }.
            if (!isWorkmanLaborType) {
                // Clear when type isn't Workman Labor so stale values don't persist
                return { ...payload, workmanTimeLogs: [] };
            }
            // On update, workman time logs are managed by dedicated endpoints
            // (Start/End Task buttons and the in-card Save). Re-sending them via
            // the Update Task API causes duplicate rows to be appended, so strip
            // the field from the update payload.
            if (isUpdate) {
                const rest = { ...payload };
                delete rest.workmanTimeLogs;
                return rest;
            }
            const logs = Array.isArray(payload.workmanTimeLogs) ? payload.workmanTimeLogs : [];
            // Drop empty/placeholder rows — backend rejects null DateTime values.
            const serialized = logs
                .filter((log) => log?.startDateTime && log?.endDateTime)
                .map((log) => ({
                    ...(log?.id ? { id: log.id } : {}),
                    startDateTime: moment(log.startDateTime).format('YYYY-MM-DDTHH:mm:ss'),
                    endDateTime: moment(log.endDateTime).format('YYYY-MM-DDTHH:mm:ss'),
                }));
            return {
                ...payload,
                workmanTimeLogs: serialized,
            };
        };

        if (Number(id) > 0) {
            return applyWorkmanFields({ ...formData, isActive: true }, { isUpdate: true });
        }

        const baseData = { ...formData };
        if (isSuperAdminUser) {
            baseData.taskForCompanyId = formData.companyId;
            baseData.taskForSellerId = formData.sellerId;
        }
        return applyWorkmanFields(baseData, { isUpdate: false });
    };

    // Helper function to submit task (create or update)
    const submitTaskData = async (taskData) => {
        const isUpdate = Number(id) > 0;
        return isUpdate ? updateTask(taskData) : createTask(taskData);
    };

    // Helper function to handle success response
    const handleTaskSuccess = (response) => {
        const newAttachments = formData.attachments.filter(file => file.isNew);
        const hasNewAttachments = newAttachments.length > 0;

        if (hasNewAttachments) {
            const taskId = Number(id) > 0 ? id : response.data.Entity;
            handleSaveAttachments(taskId);
            return;
        }

        setShowLoader(false);
        const isUpdate = Number(id) > 0;
        const message = isUpdate ? 'Task Updated successfully!' : 'Task created successfully!';
        toast.success(message);
        navigate('/tasks');
    };

    // Helper function to extract error message from response
    const getErrorMessage = (response) => {
        return response?.data?.Message?.AppStatusDescription ||
            response?.data?.Message ||
            'Failed to create/update task. Please try again.';
    };

    // Helper function to check if response indicates success
    const isSuccessResponse = (response) => {
        return response?.data?.Status === 200;
    };

    const handleSaveTask = async () => {
        const errors = validateForm(formData);
        if (Object.keys(errors).length > 0) {
            console.log('Validation Errors:', errors);
            return;
        }

        try {
            setShowLoader(true);
            const taskData = prepareTaskData();
            const response = await submitTaskData(taskData);

            if (isSuccessResponse(response)) {
                handleTaskSuccess(response);
            } else {
                setShowLoader(false);
                if (!response?.data?._readOnlyBlocked) {
                    const apiErrorMessage = getErrorMessage(response);
                    toast.error(apiErrorMessage);
                }
            }
        } catch (error) {
            setShowLoader(false);
            if (!error?.response?.data?._readOnlyBlocked) {
                toast.error('An error occurred while creating the task.');
            }
        }
    };

    const handleCancelPdfPopUp = () => {
        setShowPopUp(!showPopUp);
    };

    const handleUploadPdfAction = (file, fileDetail) => {
        handleCancelPdfPopUp();
    }

    const handleSaveAttachments = async (taskId) => {
        const formDataToSend = new FormData();
        let newFiles = formData.attachments = formData.attachments.filter(file => file.isNew);
        newFiles.forEach(file => {
            formDataToSend.append('files', file);
        });
        formDataToSend.append('TaskId', taskId);

        try {
            let response = await updateTaskAttachments(formDataToSend);
            if (response.status === 200) {
                setShowLoader(false);
                toast.success(Number(id) > 0 ? 'Task Updated successfully!' : 'Task created successfully!');
                navigate('/tasks');
            } else {
                setShowLoader(false);
                toast.error('Failed to save attachments. Please try again.');
            }
        } catch (error) {
            setShowLoader(false);
            toast.error('An error occurred while saving attachments.');
        }
    }

    const handleResetForm = () => {
        if (Number(id) > 0) {
            fetchTaskDetails();
        } else {
            setFormData({
                taskTypeId: 0,
                taskPriorityId: 0,
                taskTitle: '',
                taskStatusId: 0,
                sku: '',
                quantity: '',
                instructions: '',
                attachments: [],
                amount: 0,
                companyId: 0,
                sellerId: 0,
                workmanTimeLogs: [],
            });
        }
        setFormReset(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
        setErrors({
            ...errors,
            [name]: '', // Clear error for the field being edited
        });
        setFormReset(false);
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value.trim(),
        });
    }

    const getTaskCompanyOption = () => companyList?.find((company) => Number(company.value) === Number(formData.companyId)) || {
        Id: formData.companyId,
        value: formData.companyId,
        label: formData.companyName || formData.company || 'Selected Company',
        name: formData.companyName || formData.company || 'Selected Company',
    };

    const getTaskSellerOption = () => sellerList?.find((seller) => Number(seller.value) === Number(formData.sellerId)) || {
        value: formData.sellerId,
        label: formData.sellerName || formData.sellerAndCompany || 'Selected Seller',
    };

    const getTaskTopUpData = () => ({
        topupCompanyId: getTaskCompanyOption(),
        topupSellerId: getTaskSellerOption(),
        amount: formData.amount || 0,
    });

    const renderTaskTopUpModal = () => showTopUpModal && (
        <TopUpModal
            show={showTopUpModal}
            showModal={showTopUpModal}
            activeSellerList={companyList?.length ? companyList : [getTaskCompanyOption()]}
            onHide={() => setShowTopUpModal(false)}
            fundList={[]}
            filterData={{}}
            initialTopUpData={getTaskTopUpData()}
        />
    );

    // Normalize workman time logs from server response (supports legacy single start/end fields)
    const normalizeWorkmanLogsFromServer = (entity) => {
        const serverLogs = Array.isArray(entity?.workmanTimeLogs) ? entity.workmanTimeLogs : [];
        if (serverLogs.length > 0) {
            return serverLogs.map((log) => ({
                id: log?.id,
                startDateTime: log?.startDateTime ? new Date(log.startDateTime) : null,
                endDateTime: log?.endDateTime ? new Date(log.endDateTime) : null,
            }));
        }
        if (entity?.workmanStartDateTime || entity?.workmanEndDateTime) {
            return [{
                startDateTime: entity.workmanStartDateTime ? new Date(entity.workmanStartDateTime) : null,
                endDateTime: entity.workmanEndDateTime ? new Date(entity.workmanEndDateTime) : null,
            }];
        }
        return [];
    };

    const fetchTaskDetails = async (preserveEditable = false) => {
        setShowLoader(true);
        let res = await getTaskById(id);
        if (res.data.Status === 200) {
            if (!preserveEditable) {
                setIsEditable(!location?.state?.editable ? false : res.data.Entity.isEditable);
            }
            setShowLoader(false);
            const stateData = location?.state?.data || {};
            const refType = res.data.Entity.taskReferenceType;
            let derivedCategory;
            if (refType === 'Package') derivedCategory = 'ReturnPackage';
            else if (refType === 'Product') derivedCategory = 'ReturnProduct';
            else if (refType === 'General') derivedCategory = 'General';
            let obj = {
                ...res.data.Entity,
                taskId: res.data.Entity.id,
                taskCategory:
                    derivedCategory || res.data.Entity.taskCategory || stateData.taskCategory,
                packageTrackingNumber:
                    res.data.Entity.packageTrackingNumber || stateData.packageTrackingNumber,
                workmanTimeLogs: normalizeWorkmanLogsFromServer(res.data.Entity),
            }
            setFormData(obj);
        } else {
            setShowLoader(false);
            console.log('Error Log:', res.data);
        }
    };

    const handleDeleteAttachment = (item, index) => {
        let formDataClone = _.cloneDeep(formData);
        if (item.isNew) {
            formDataClone.attachments.splice(index, 1);
        } else {
            formDataClone.attachments[index].isDeleted = true;
            formDataClone.attachmentActions = formDataClone.attachmentActions ?? [];
            formDataClone.attachmentActions.push(formDataClone.attachments[index]);
            formDataClone.attachments.splice(index, 1);
        }
        setFormData(formDataClone);
    };

    const handleDownload = async (item) => {
        let response = await handleDownloadFile(encodeURIComponent(item.fileUrl));
        if (response.status === 200) {
            const blob = response.data;
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', item.name);
            document.body.appendChild(link); // NOSONAR
            link.click();
            document.body.removeChild(link); // NOSONAR
        } else {
            toast.error('Failed to download file.');
        }
    }

    const handleAttachments = (e) => {
        let files = [...e.target.files];
        try {
            if (files.length === 0) return;
            if (files.length > MAX_FILE_COUNT) {
                toast.info(`Only ${MAX_FILE_COUNT} files can be uploaded at one time`, 1000);
                e.target.value = "";
                return;
            }

            // Validate extensions and individual file size
            let validFiles = [];
            let totalSizeBytes = 0;

            files.forEach((file) => {
                const extension = file.name.split(".").pop()?.toLowerCase();
                const fileSizeBytes = file.size;

                if (!allowedDocExtensions.includes(extension)) {
                    toast.error(`Unsupported file format: ${file.name}`, 1000);
                    return;
                }
                totalSizeBytes += fileSizeBytes;
                file.isNew = true;
                validFiles.push(file);
            });

            // Check total size
            if (totalSizeBytes > maxSize * 1024 * 1024) {
                toast.info(`Total file size should not exceed ${maxSize}MB`, 1000);
                e.target.value = "";
                return;
            }

            if (validFiles.length > 0) {
                setFormData((prev) => ({
                    ...prev,
                    attachments: [...prev.attachments, ...validFiles],
                }));
            }

            e.target.value = ""; // Reset input
        } catch (err) {
            console.log(err);
        }
    };

    const handleEditRecord = () => {
        if (formData.isEditable) {
            setIsEditable(!isEditable);
        };
    };

    const handleDownloadAll = async () => {
        let oldAttachments = formData.attachments.filter(file => !file.isNew);
        let response = await handleDownloadAllFiles(oldAttachments);
        if (response.status === 200) {
            const blob = response.data;
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const disposition = response.headers['content-disposition'];
            let filename = `Attachments_${new Date().toISOString()}.zip`;
            if (disposition && disposition.indexOf('filename=') !== -1) {
                filename = disposition
                    .split('filename=')[1]
                    .split(';')[0]
                    .replace(/"/g, ''); //NOSONAR
            }
            link.setAttribute('download', filename);
            document.body.appendChild(link); // NOSONAR
            link.click();
            document.body.removeChild(link); // NOSONAR
        } else {
            toast.error('Failed to download file.');
        }
    };

    const handleDateFieldChange = (date) => {
        setFormData({
            ...formData,
            entryDate: date
        });
    };

    const handleViewImage = (item) => {
        let data = {
            ...item,
            imageUrl: item.fileUrl
        }
        setSelectedImageRecord(data);
        setShowImageModal(true);
    };

    const handleChargesDetails = () => {
        setShowChargesDetails(!showChargesDetails);
    };

    const handleOverrideCharge = () => {
        setShowCharge(!showCharge);
        setShowChargeAlert(false);
    };

    return (
        <main>
            <div className="main__container">
                <div className="main__tile_heading cus-btn-include-sec">
                    <span>{Number(id) ? 'Update' : 'Create'} {(() => {
                        const category = formData?.taskCategory || location?.state?.data?.taskCategory;
                        if (category === 'ReturnPackage') return 'Return Package Task';
                        if (category === 'ReturnProduct') return 'Return Product Task';
                        return 'Task';
                    })()} {Number(id) > 0 && `(# ${Number(id)})`} {formData?.id && formData?.postCharges?.length > 0 && <button id='chargesBtn' className='sortBtn fontFix' onClick={handleChargesDetails}><i className='fa fa-info-circle' aria-hidden="true" style={{color: "#ff0000"}}></i></button>}</span>

                    <div className="top_right_button_container">
                        <div className="top_right_button_container">    
                            {isDefaultSuperAdminUser && Number(id) > 0 && (
                                <button
                                    className="btn cus-seconday-bg-btn top-btn"
                                    onClick={openChargeSellerDialog}
                                >
                                    Post Charges
                                </button>
                            )}
                            <button
                                className="btn cus-seconday-bg-btn top-btn"
                                onClick={handleSaveTask}
                                disabled={!isEditable}
                            >
                                Save
                            </button>
                            <button
                                className="btn cus-seconday-bg-btn top-btn"
                                onClick={handleResetForm}
                                disabled={!isEditable || formReset}
                            >
                                Reset Form
                            </button>
                            <button
                                className="btn cus-seconday-bg-btn top-btn"
                                onClick={handleEditRecord}
                                disabled={!formData.isEditable || isEditable}
                            >
                                Edit
                            </button>
                            <button
                                className="btn cus-primary-transparent-btn bckbtn"
                                onClick={() => navigate('/tasks')}
                            >
                                Back
                            </button>
                        </div>
                    </div>
                </div>
                {showLoader && <div className="full-screen-spinner-overlay zIndex2"><LoaderSpinner /></div>}
                <div className="incoming_wrapper main_content_table fontFix addressDetailWrapper">
                    <div className='row mb-3'>
                        <div className='col-lg-6 col-md-6 col-sm-12 mobMb-10'>
                            <div className="input-label-txt">Type <span className="require-field">*</span></div>
                            <select
                                className="form-control"
                                value={formData.taskTypeId}
                                name="taskTypeId"
                                onChange={handleInputChange}
                                disabled={!isEditable}
                            >
                                <option value="0">Select Task Type</option>
                                {allTaskTypeList?.map((item, index) => (
                                    <option key={item.id} value={item.id}>{item.typeName}</option>
                                ))}
                            </select>
                            {errors.taskTypeId && <div className="requiredthis">*This is required</div>}
                        </div>
                        <div className='col-lg-6 col-md-6 col-sm-12 mobMb-10'>
                            <div className="input-label-txt">Priority <span className="require-field">*</span></div>
                            <select
                                className="form-control"
                                value={formData.taskPriorityId}
                                name="taskPriorityId"
                                onChange={handleInputChange}
                                disabled={!isEditable}
                            >
                                <option value="0">Select Priority</option>
                                {allTaskPriorityList?.map((item, index) => (
                                    <option key={item.id} value={item.id}>{item.priorityName}</option>
                                ))}
                            </select>
                            {errors.taskPriorityId && <div className="requiredthis">*This is required</div>}
                        </div>
                    </div>
                    <div className='row mb-3'>
                        <div className={`col-lg-${isSuperAdminUser ? '6' : '12'} col-md-12 col-sm-12 mobMb-10`}>
                            <div className="input-label-txt">Title <span className="require-field">*</span></div>
                            <input
                                className="form-control"
                                placeholder="Enter task title"
                                type="text"
                                value={formData.taskTitle || ''}
                                name="taskTitle"
                                onChange={handleInputChange}
                                disabled={!isEditable}
                                onBlur={handleBlur}
                            />
                            {errors.taskTitle && <div className="requiredthis">*This is required</div>}
                        </div>
                        {isSuperAdminUser && (
                            <>
                                <div className='col-lg-3 col-md-6 col-sm-12 mobMb-10'>
                                    <div className="input-label-txt">Company <span className="require-field">*</span></div>
                                    <div className="select_react">
                                        <Select
                                            options={companyList}
                                            onChange={(opt) => {
                                                const val = opt?.value || 0;
                                                setFormData(prev => ({ ...prev, companyId: val, sellerId: 0 }));
                                                // fetch sellers for selected company (create flow)
                                                if (Number(id) === 0) {
                                                    dispatch(getSellersforMultipleCompany([val]));
                                                }
                                            }}
                                            className="activeSellerListInput"
                                            // Disable company select for existing tasks (shouldn't be editable)
                                            isDisabled={Number(id) > 0 || !isEditable}
                                            value={companyList?.find(c => c.value === formData.companyId) || null}
                                            placeholder="Select Company..."
                                            isMulti={false}
                                            classNamePrefix="list-value"
                                            styles={{
                                                control: (provided) => ({
                                                    ...provided,
                                                    minHeight: '28px',
                                                    height: '36px',
                                                    fontSize: '13px',
                                                    paddingTop: '0.5px'
                                                }),
                                                placeholder: (provided) => ({
                                                    ...provided,
                                                    color: '#515558ff'
                                                })
                                            }}
                                        />
                                    </div>
                                    {errors.companyId && <div className="requiredthis">*This is required</div>}
                                </div>
                                <div className='col-lg-3 col-md-6 col-sm-12 mobMb-10'>
                                    <div className="input-label-txt">Seller <span className="require-field">*</span></div>
                                    <div className="select_react">
                                        <Select
                                            options={sellerList}
                                            onChange={(opt) => setFormData(prev => ({ ...prev, sellerId: opt?.value || 0 }))}
                                            className="activeSellerListInput"
                                            // Disable seller select for existing tasks (shouldn't be editable)
                                            isDisabled={Number(id) > 0 || !isEditable}
                                            value={sellerList?.find(s => s.value === formData.sellerId) || null}
                                            placeholder="Select Seller..."
                                            isMulti={false}
                                            classNamePrefix="list-value"
                                            styles={{
                                                control: (provided) => ({
                                                    ...provided,
                                                    minHeight: '28px',
                                                    height: '36px',
                                                    fontSize: '13px',
                                                    paddingTop: '0.5px'
                                                }),
                                                placeholder: (provided) => ({
                                                    ...provided,
                                                    color: '#515558ff'
                                                })
                                            }}
                                        />
                                    </div>
                                    {errors.sellerId && <div className="requiredthis">*This is required</div>}
                                </div>
                            </>
                        )}
                    </div>
                    <div className='row mb-3'>
                        <div className='col-lg-3 col-md-6 col-sm-12 mobMb-10'>
                            <div className="input-label-txt">Status <span className="require-field">*</span></div>
                            <select
                                className="form-control"
                                value={formData.taskStatusId ?? 0}
                                name="taskStatusId"
                                onChange={handleInputChange}
                                disabled={!isSuperAdminUser || !isEditable}
                            >
                                {allTaskStatusList?.map((item) => (
                                    <option key={item.id} value={item.id}>{item.statusName}</option>
                                ))}
                            </select>
                            {errors.taskStatusId && <div className="requiredthis">*This is required</div>}
                        </div>
                        {isWalletRechargeTaskType ? (
                            <div className='col-lg-3 col-md-6 col-sm-12 mobMb-10'>
                                <div className="input-label-txt">Amount ($)</div>
                                <div className="input-group taskTopUpInputGroup">
                                    <input
                                        className={`form-control`}
                                        type={"number"}
                                        name="amount"
                                        maxLength={10}
                                        placeholder="Enter Amount"
                                        value={formData.amount || ''}
                                        onChange={handleInputChange}
                                        disabled={!isEditable}
                                        onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
                                        onBlur={handleBlur}
                                        onScroll={(e) => e.target.blur()}
                                    />
                                    {Number(id) > 0 && isSuperAdminUser && (
                                        <button
                                            type="button"
                                            className="btn cus-seconday-bg-btn taskTopUpBtn"
                                            onClick={() => setShowTopUpModal(true)}
                                            disabled={!formData.companyId || !formData.sellerId || !formData.amount}
                                            title="Top Up"
                                            aria-label="Top Up"
                                        >
                                            <img src={TopUpIconWhite} alt="" />
                                            <span>Top Up</span>
                                        </button>
                                    )}
                                </div>
                                {errors.amount && <div className="requiredthis">*This is required</div>}
                            </div>
                        ) : (
                            <>
                                <div className='col-lg-3 col-md-6 col-sm-12 mobMb-10'>
                                    {formData?.taskCategory === 'ReturnPackage' ? (
                                        <>
                                            <div className="input-label-txt">Tracking Number</div>
                                            <input
                                                className={`form-control outboundNumber`}
                                                type="text"
                                                name="packageTrackingNumber"
                                                placeholder="Tracking Number"
                                                value={formData.packageTrackingNumber || ''}
                                                disabled
                                                maxLength={50}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <div className="input-label-txt">SKU</div>
                                            <input
                                                className={`form-control outboundNumber`}
                                                type="text"
                                                name="sku"
                                                placeholder="Enter SKU"
                                                value={formData.sku || ''}
                                                onChange={handleInputChange}
                                                disabled={!isEditable || formData?.taskCategory === 'ReturnProduct'}
                                                onBlur={handleBlur}
                                                maxLength={30}
                                            />
                                        </>
                                    )}
                                </div>
                            </>
                        )}
                        <div className='col-lg-3 col-md-6 col-sm-12 mobMb-10'>
                            <div className="input-label-txt">Quantity</div>
                            <input
                                className={`form-control outboundNumber`}
                                type="number"
                                name="quantity"
                                placeholder="Enter Quantity"
                                value={formData.quantity || ''}
                                maxLength={10}
                                onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
                                onChange={handleInputChange}
                                disabled={!isEditable || formData?.taskCategory === 'ReturnProduct' || formData?.taskCategory === 'ReturnPackage'}
                                onBlur={handleBlur}
                            />
                        </div>
                        <div className="col-lg-3 col-md-6 col-sm-12 mobMb-10">
                            <div className="input-label-txt">Date</div>
                            <DatePickerComponent
                                dateField={formData.entryDate}
                                handleDateChange={(date) => handleDateFieldChange(date)}
                                fieldName="entryDate"
                                dateFormat="MM-dd-yyyy"
                                disabled={!isEditable || !isSuperAdminUser}
                                onKeyDown={(e) => {
                                    e.preventDefault();
                                }}
                            />
                        </div>
                    </div>
                    {showWorkmanSection && (
                        <div className="row mb-3">
                            <div className="col-12">
                                <div className="workmanTimeLogCard">
                                    <div className="workmanTimeLogCard__header">
                                        <span className="workmanTimeLogCard__title">
                                            <i className="fa fa-clock-o" aria-hidden="true"></i>
                                            <strong className="ml-2">Workman time log</strong>
                                        </span>
                                        {showWorkmanActionButtons && (
                                            <div className="workmanTimeLogCard__actions">
                                                {!hasOpenWorkmanLog && (
                                                    <button
                                                        type="button"
                                                        className="btn cus-primary-bg-btn"
                                                        onClick={handleStartWorkmanTask}
                                                        disabled={!isEditable}
                                                    >
                                                        <i className="fa fa-play" aria-hidden="true"></i>{' '}
                                                        Start Task
                                                    </button>
                                                )}
                                                {hasOpenWorkmanLog && (
                                                    <button
                                                        type="button"
                                                        className="btn cus-seconday-bg-btn"
                                                        onClick={handleEndWorkmanTask}
                                                        disabled={!isEditable}
                                                    >
                                                        <i className="fa fa-stop" aria-hidden="true"></i>{' '}
                                                        End Task
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="workmanTimeLogCard__body">
                                        {(formData.workmanTimeLogs || []).length === 0 && isWarehouseUser && !isSuperAdminUser && (
                                            <div className="workmanTimeLogCard__empty">
                                                No time logs yet. Click <strong>Start Task</strong> to begin tracking time.
                                            </div>
                                        )}
                                        {(formData.workmanTimeLogs || []).map((log, idx) => {
                                            const rowErr = errors?.workmanTimeLogs?.[idx] || {};
                                            const rangeErr = getRowRangeError(log);
                                            const rowDurationLabel = computeDurationLabel(log?.startDateTime, log?.endDateTime);
                                            const canRemove = canEditWorkmanInputs && (formData.workmanTimeLogs || []).length > 1;
                                            return (
                                                <div className="workmanTimeLogCard__logRow" key={log?.id ?? `new-${idx}`}>
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <span className="workmanTimeLogCard__logIndex">Log #{idx + 1}</span>
                                                        {canRemove && (
                                                            <button
                                                                type="button"
                                                                className="workmanTimeLogCard__removeBtn"
                                                                onClick={() => handleRemoveWorkmanLog(idx)}
                                                                aria-label={`Remove log ${idx + 1}`}
                                                            >
                                                                <i className="fa fa-trash" aria-hidden="true"></i>
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div className="row">
                                                        <div className="col-lg-3 col-md-6 col-sm-12 mobMb-10">
                                                            <div className="input-label-txt">
                                                                Start datetime {isWorkmanLaborType && <span className="require-field">*</span>}
                                                            </div>
                                                            <DatePickerComponent
                                                                dateField={log?.startDateTime || null}
                                                                handleDateChange={(date) =>
                                                                    handleWorkmanLogChange(idx, 'startDateTime', date)
                                                                }
                                                                fieldName={`workmanLogStart_${idx}`}
                                                                dateFormat="MM-dd-yyyy HH:mm"
                                                                timeSelect={true}
                                                                disabled={!canEditWorkmanInputs}
                                                            />
                                                            {rowErr.startDateTime && (
                                                                <div className="requiredthis">*{rowErr.startDateTime}</div>
                                                            )}
                                                        </div>
                                                        <div className="col-lg-3 col-md-6 col-sm-12 mobMb-10">
                                                            <div className="input-label-txt">
                                                                End datetime {isWorkmanLaborType && <span className="require-field">*</span>}
                                                            </div>
                                                            <DatePickerComponent
                                                                dateField={log?.endDateTime || null}
                                                                handleDateChange={(date) =>
                                                                    handleWorkmanLogChange(idx, 'endDateTime', date)
                                                                }
                                                                fieldName={`workmanLogEnd_${idx}`}
                                                                dateFormat="MM-dd-yyyy HH:mm"
                                                                timeSelect={true}
                                                                disabled={!canEditWorkmanInputs}
                                                            />
                                                            {(rowErr.endDateTime || rangeErr) && (
                                                                <div className="requiredthis">
                                                                    *{rowErr.endDateTime || rangeErr}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="col-lg-3 col-md-6 col-sm-12 mobMb-10">
                                                            <div className="input-label-txt">Duration</div>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                value={rowDurationLabel}
                                                                readOnly
                                                                placeholder="0h 0m"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {canEditWorkmanInputs && (
                                            <div className="mb-3 d-flex align-items-center justify-content-between flex-wrap">
                                                <button
                                                    type="button"
                                                    className="workmanTimeLogCard__addBtn"
                                                    onClick={handleAddWorkmanLog}
                                                >
                                                    <i className="fa fa-plus" aria-hidden="true"></i>{' '}
                                                    Add another time log
                                                </button>
                                                {/* Save time logs button removed: time logs are persisted
                                                    via the dedicated Start/End Task buttons. Showing a
                                                    separate Save action caused duplicate rows to be
                                                    created when combined with those endpoints. */}
                                            </div>
                                        )}
                                        {(formData.workmanTimeLogs || []).length > 0 && (
                                            <div className="workmanTimeLogCard__summary">
                                                <div className="workmanTimeLogCard__summaryLeft">
                                                    <span className="workmanTimeLogCard__duration">
                                                        {formatDurationLabel(totalWorkmanMinutes)}
                                                    </span>
                                                    <span className="workmanTimeLogCard__totalLabel">
                                                        Total time spent
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                        <div className="workmanTimeLogCard__footer">
                                            Duration is automatically calculated from start and end date &amp; time for each log; the total is summed across all logs.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className='row mb-3'>
                        <div className="col col-lg-6 col-md-6 col-sm-12 mobMb-10">
                            <div className="input-label-txt">Attachments <i id="uploadInfo" className="fa fa-info-circle" aria-hidden="true"></i></div>
                            <WmsTooltip
                                anchorId={`uploadInfo`}
                                content={`Upload files to attach to this task. You can upload multiple files here. Max file size is ${maxSize}MB.`}
                                place="top"
                                className="tooltipTxt"
                            />
                            <div className="uploadContainer">
                                <div className="inlineBlock fileUpload inventory">
                                    <button
                                        type="button"
                                        className="btn cus-seconday-bg-btn"
                                        disabled={!isEditable}
                                    >
                                        Upload documents
                                    </button>
                                    <input
                                        className="hiddenFileField"
                                        type="file"
                                        id="customFile"
                                        multiple
                                        onChange={handleAttachments}
                                        disabled={!isEditable}
                                    />
                                </div>
                                <button className='ml-2 inlineBlock fileUpload inventory link sortBtn' onClick={handleCancelPdfPopUp}>
                                    Upload & Merge Pdf Files <i id="uploadMerge" className="fa fa-info-circle" aria-hidden="true"></i>
                                </button>
                                <WmsTooltip
                                    anchorId={`uploadMerge`}
                                    content={`Upload PDFs to combine, download the merge file, then re-upload it here.`}
                                    place="top"
                                    className="tooltipTxt"
                                />
                                <div>
                                    {formData.attachments.filter(item => item.fileUrl).length > 0 &&
                                        <button className='btn mt-1 mr-2 downloadAll' onClick={handleDownloadAll}><i className="fa fa-download" aria-hidden="true"></i> Download All</button>
                                    }
                                </div>
                                <div className='displayFlex attachmentsFlex'>
                                    {formData.attachments.map((item, index) => (
                                        <Fragment key={item?.id}>
                                            {!item.isDeleted && item.fileUrl && (
                                                <div className={`selectedFile mr-2`} key={item?.id}>
                                                    <div className="fileName">
                                                        {/\.(jpg|jpeg|png|gif|webp)$/i.test(item.name) ? (
                                                            <button className='sortBtn imagePreview' onClick={() => handleViewImage(item)}>
                                                                <img
                                                                src={item.fileUrl}
                                                                alt={item.name}
                                                                style={{ width: "100px", height: "auto", borderRadius: "8px" }}
                                                                />
                                                            </button>
                                                        ) : (
                                                            <a
                                                            href={item.fileUrl}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            >
                                                            {item.name}
                                                            </a>
                                                        )}
                                                    </div>
                                                    {isEditable && (
                                                        <button
                                                            className="deleteFile"
                                                            onClick={() =>
                                                                handleDeleteAttachment(item, index)
                                                            }
                                                            disabled={!isEditable}
                                                        >
                                                            <i className="fa fa-times" aria-hidden="true"></i>
                                                        </button>
                                                    )}
                                                    <button
                                                        className="downBtn ml-2"
                                                        onClick={() =>
                                                            handleDownload(item)
                                                        }
                                                    >
                                                        <i className="fa fa-download" aria-hidden="true"></i>
                                                    </button>
                                                </div>
                                            )}
                                        </Fragment>
                                    ))}
                                </div>
                                <div className='displayFlex alignItemsCenter flexWrap wrapTasks'>
                                    {formData.attachments.map((item, index) => (
                                        <Fragment key={item?.id}>
                                            {!item.fileUrl && (
                                                <div className={`selectedFile mr-2 filePreview`} key={item?.id}>
                                                    <div className="fileName">
                                                        <div>
                                                            {item.name}
                                                            <InlineFilePreview
                                                                file={item}
                                                                onClose={() => handleDeleteAttachment(item, index)}
                                                            />
                                                        </div>
                                                    </div>
                                                    {isEditable && (
                                                        <button
                                                            className="deleteFile"
                                                            onClick={() => handleDeleteAttachment(item, index)}
                                                            type="button"
                                                        >
                                                            <i className="fa fa-times" aria-hidden="true"></i>
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </Fragment>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='row mb-3'>
                        <div className='col-lg-12 col-md-12 col-sm-12 mobMb-10'>
                            <div className="input-label-txt" htmlFor="instructions">Comment</div>
                            <textarea
                                onBlur={handleBlur}
                                className="form-control"
                                placeholder="Enter any comment"
                                rows="7"
                                value={formData?.instructions}
                                name="instructions"
                                id="instructions"
                                onChange={handleInputChange}
                                disabled={!isEditable}
                            ></textarea>
                        </div>
                    </div>
                </div>
            </div>
            {showCharge && (
                <ChargeSeller show={showCharge} taskId={id} handleCancel={() => setShowCharge(false)} fetchTaskDetails={fetchTaskDetails}/>
            )}
            {renderTaskTopUpModal()}
            {showPopUp && (
                <PdfMergePopup handleCancel={handleCancelPdfPopUp} showPopUp={showPopUp} handleUploadPdfAction={handleUploadPdfAction} />
            )}

            {showImageModal && (
                <ViewImage
                    selectedRecord={selectedImageRecord}
                    showHideImageModal={showImageModal}
                    setShowHideImageModal={setShowImageModal}
                    title="Preview"
                />
            )}

            {showChargesDetails && (
                <ChargesHistory
                    show={showChargesDetails} 
                    handleCancel={() => setShowChargesDetails(false)} 
                    data={formData}
                />
            )}
            <Tooltip anchorId="chargesBtn" place="right" className="tooltipWidth fontFix">
                <div>Show Charges History</div>
            </Tooltip>

            <AlertDialogModal
                modalShow={showChargeAlert}
                handleCancelDeleteAlert={() => setShowChargeAlert(false)}
                modalClass={"modalDeleteWrapper"}
                messageText="This task has already been charged. Do you want to charge it again?"
                handleYesDeleteAlert={handleOverrideCharge}
                headingText={"POST CHARGES"}
            />
        </main >
    );
}

export default Tasks;
